/* ==========================================================================
   Bauer & Meyer — collection.js
   <facets-results data-section-id="…">: in-place filtering, sorting, pagination
   and "Meer laden" for main-collection and main-search. Without JS the GET form
   and plain links keep working.

   Regions inside the section are marked with data-facets-region="<name>"; after a
   fetch of `?section_id=` every region is swapped. The result count is a
   persistent aria-live node ([data-facets-count]) whose text is updated in place.
   ========================================================================== */

(() => {
  if (customElements.get('facets-results')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  class FacetsResults extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.debouncedSubmit = this.debounce(() => this.submit(), 500);

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('submit', (e) => this.onSubmit(e));
      this.addEventListener('click', (e) => this.onClick(e));

      this.lastSearch = window.location.search;
      this.onPopState = () => {
        if (window.location.search === this.lastSearch) return; // hash-only navigation
        this.load(window.location.search, { push: false });
      };
      window.addEventListener('popstate', this.onPopState);

      this.syncMode = this.syncMode.bind(this);
      desktop.addEventListener('change', this.syncMode);
      this.syncMode();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      desktop.removeEventListener('change', this.syncMode);
    }

    debounce(fn, wait) {
      let t;
      return () => { clearTimeout(t); t = setTimeout(fn, wait); };
    }

    get form() { return this.querySelector('[data-facets-form]'); }
    get drawer() { return this.querySelector('drawer-panel'); }

    /* Sidebar on desktop is a plain landmark; everywhere else it's a modal drawer. */
    syncMode() {
      const panel = this.querySelector('[data-facets-panel]');
      if (!panel) return;
      const isSidebar = desktop.matches && this.drawer && this.drawer.classList.contains('facets--sidebar');
      if (isSidebar) {
        if (this.drawer.isOpen) this.drawer.close();
        ['role', 'aria-modal', 'aria-hidden'].forEach((a) => panel.removeAttribute(a));
      } else {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
      }
    }

    onChange(e) {
      const field = e.target;
      if (!this.form || field.form !== this.form) return;
      // Price inputs wait a little longer so typing "1", "15", "150" is one request
      if (field.type === 'number') this.debouncedSubmit();
      else this.submit();
    }

    onSubmit(e) {
      if (e.target !== this.form) return;
      e.preventDefault();
      this.submit();
      if (this.drawer && this.drawer.isOpen) this.drawer.close();
    }

    onClick(e) {
      const close = e.target.closest('a[data-drawer-close]');
      if (close) { e.preventDefault(); return; }

      const more = e.target.closest('[data-load-more]');
      if (more) { e.preventDefault(); this.loadMore(more); return; }

      const link = e.target.closest('a[data-facet-link]');
      if (link) {
        e.preventDefault();
        this.load(new URL(link.href, window.location.origin).search, { scroll: link.hasAttribute('data-scroll') });
      }
    }

    query() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      return params.toString();
    }

    submit() {
      if (!this.form) return;
      this.load(`?${this.query()}`);
    }

    sectionUrl(search) {
      const params = new URLSearchParams(search);
      params.delete('section_id');
      params.set('section_id', this.sectionId);
      return `${window.location.pathname}?${params.toString()}`;
    }

    async fetchSection(url) {
      if (this.cache.has(url)) return this.cache.get(url);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      this.cache.set(url, html);
      return html;
    }

    async load(search, { push = true, scroll = false } = {}) {
      const query = new URLSearchParams(search);
      query.delete('section_id');
      const clean = query.toString();
      const pageUrl = `${window.location.pathname}${clean ? `?${clean}` : ''}`;

      this.setBusy(true);
      try {
        const html = await this.fetchSection(this.sectionUrl(clean));
        this.render(parse(html));
        if (push) history.pushState({ facets: this.sectionId }, '', pageUrl);
        this.lastSearch = window.location.search;
        if (scroll) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        window.location.href = pageUrl; // fall back to a full page load
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      const results = this.querySelector('[data-facets-region="results"]');
      if (!results) return;
      results.classList.toggle('is-loading', busy);
      results.toggleAttribute('aria-busy', busy);
    }

    render(doc) {
      const fresh = doc.querySelector(`facets-results[data-section-id="${this.sectionId}"]`);
      if (!fresh) throw new Error('Section not found');

      // Remember which accordions were open/closed and what had focus
      const openState = new Map();
      this.querySelectorAll('details[id]').forEach((d) => openState.set(d.id, d.open));
      const focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      fresh.querySelectorAll('[data-facets-region]').forEach((region) => {
        const current = this.querySelector(`[data-facets-region="${region.dataset.facetsRegion}"]`);
        if (current) current.replaceWith(document.importNode(region, true));
      });

      openState.forEach((open, id) => {
        const details = document.getElementById(id);
        if (details && this.contains(details)) details.open = open;
      });
      if (focusedId) {
        const el = document.getElementById(focusedId);
        if (el) el.focus({ preventScroll: true });
      }

      // Update live text in place so screen readers announce the new count
      const count = this.querySelector('[data-facets-count]');
      const freshCount = fresh.querySelector('[data-facets-count]');
      if (count && freshCount) count.textContent = freshCount.textContent;

      const toggle = this.querySelector('.facets-toggle');
      if (toggle && this.drawer) toggle.setAttribute('aria-expanded', this.drawer.isOpen ? 'true' : 'false');
      document.dispatchEvent(new CustomEvent('facets:updated', { detail: { sectionId: this.sectionId } }));
    }

    async loadMore(button) {
      const grid = this.querySelector('[data-product-grid]');
      if (!grid || button.classList.contains('is-loading')) return;
      const hadFocus = document.activeElement === button;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      try {
        const url = new URL(button.href, window.location.origin);
        const html = await this.fetchSection(this.sectionUrl(url.search));
        const doc = parse(html);
        const items = Array.from(doc.querySelectorAll('[data-product-grid] > li:not([data-promo])'))
          .map((li) => document.importNode(li, true));
        grid.append(...items);

        const pagination = this.querySelector('[data-facets-region="pagination"]');
        const freshPagination = doc.querySelector('[data-facets-region="pagination"]');
        if (pagination && freshPagination) pagination.replaceWith(document.importNode(freshPagination, true));

        const statusText = doc.querySelector('[data-load-more-status]');
        const status = this.querySelector('[data-facets-status]');
        if (status && statusText) status.textContent = statusText.textContent.trim();

        if (hadFocus && items[0]) {
          const link = items[0].querySelector('a[href]:not([tabindex="-1"])');
          if (link) link.focus({ preventScroll: true });
        }
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = button.href;
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('facets-results', FacetsResults);
})();
